import { Color, Radius, Spacing } from 'zens-storybook'
import React, { Suspense, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useStore, WorkspaceData, Draft, ApiError, useWorkspaceMutation } from 'zens-store'
import { useRouter, useNumberParams } from 'zens-router'
import { LayoutHeader, NavigationDelete, WorkspaceEditForm, WorkspaceHeader } from '@dms/component'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    margin: Spacing.L,
    padding: Spacing.L,
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
  },
})

const WorkspaceEdit = () => {
  const [errors, setErrors] = useState<Record<string, string>>()
  const router = useRouter()
  const store = useStore()
  const { message } = store
  const [confirmDelete, setConfirmDelete] = useState<Draft<WorkspaceData>>()
  const [{ companyId, workspaceId }] = useNumberParams()
  const { t } = useTranslation()
  const mutation = useWorkspaceMutation({ companyId })

  const save = async (draft: Draft<WorkspaceData>) => {
    try {
      setErrors({})
      await mutation.update(draft)
      message.add({
        message: t('Workspace was updated successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage, fields } = error as ApiError
      console.debug(`WORKSPACE: Workspace failed: ${errorMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Workspace could not be updated')}. ${errorMessage}`,
          status: 'error',
        })
      }
      return
    }
    router.push(`/companies/${companyId}/workspaces/${workspaceId}`)
  }

  const remove = async () => {
    try {
      await mutation.delete(confirmDelete!)
      message.add({
        message: t('Workspace was deleted successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage } = error as Error
      console.debug(`LOCATION: Workspace deletion failed: ${errorMessage}`)
      message.add({
        message: `${t('Workspace could not be deleted')}. ${errorMessage}`,
        status: 'error',
      })
      return
    }

    router.push(`/companies/${companyId}/workspaces`)
  }

  return (
    <View style={styles.container}>
      <Suspense fallback={<LayoutHeader.Skeleton breadcrumb title />}>
        <WorkspaceHeader companyId={companyId} workspaceId={workspaceId} breadcrumbItems={[{ title: t('Edit') }]} />
      </Suspense>
      <View style={styles.content}>
        <Suspense fallback={<WorkspaceEditForm.Skeleton />}>
          <WorkspaceEditForm
            companyId={companyId}
            workspaceId={workspaceId}
            onSave={save}
            onDelete={async (draft) => setConfirmDelete(draft)}
            errors={errors}
          />
        </Suspense>
        <NavigationDelete
          entity={t('Workspace')}
          visible={!!confirmDelete}
          onClose={() => setConfirmDelete(undefined)}
          onDelete={remove}
        />
      </View>
    </View>
  )
}

export default WorkspaceEdit
